import React, { useState, useEffect, useContext } from "react";
import {
  AppBar,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { Productdata } from "../../components/grid/GridData.tsx";
import { externalgetApi } from "../../services/externalApis.tsx";
import { SETTING_API_07 } from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import {
  MigrationMessages,
  UITextMessages,
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface Product {
  productcode?: string | number;
  productname?: string;
  catname?: string;
  subcatname?: string;
  brandname?: string;
  packagename?: string;
  immeasuringunit?: string;
  minorderlevel?: number | string;
  productsize?: number | string;
  packageunits?: number | string;
  descrip?: string;
  imagepath?: string;
  [key: string]: unknown;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface Column {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
}

interface RowWithId extends Product {
  id: string | number;
}

function ViewProduct(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  
  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE } = SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filteredRows, setFilteredRows] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [data, setData] = useState<Product[] | null>(null);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setPageData({
      title: "Product",
    } as PageData);
  }, [setPageData]);

  const handleItemClick = (id: string | number, actionText: string): void => {
    if (actionText === "Edit") {
      const selectedItem = data?.find((item) => item.productcode === id);
      if (selectedItem) {
        navigate("/CreateProducts", { state: { selectedItem, actionText } });
      }
    }
  };

  const menuItems: MenuItem[] = [
    {
      text: "Edit",
      icon: <BorderColorOutlinedIcon />,
      onClick: (productcode: string | number) => handleItemClick(productcode, "Edit"),
    },
  ];

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const result = (await externalgetApi(SETTING_API_07, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        })) as Product[];

        setData(result);
        setFilteredRows(result);
        console.log("result", result);
        setIsLoading(false);
      } catch (err) {
        const error = err as ApiError;
        setError(error.message || MigrationMessages.error.failedToFetchData);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Handle search input change
  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);

    // Filter rows based on search query
    const filtered = data?.filter((row) =>
      row.productname?.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredRows(filtered || []);
  };

  const addToView = (): void => {
    navigate("/CreateProducts");
  };

  // Prepare rows with id for the grid
  const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
    ...row,
    id: row.productcode || Math.random(),
  }));

  // Placeholder processRowUpdate function
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    return newRow;
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
          >
            {UITextMessages?.breadcrumb?.home || "Home"}
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
          >
            Settings
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            aria-current="page"
          >
            Products
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {Productdata.name}
              </Typography>
            </Grid>

            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
              sx={styles.appbar_grid_search}
            >
              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={styles.ipt_appbar_search_auto}
                options={data?.map((row) => row.productname || "").filter(Boolean) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder="Search by..."
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              id="btn_search"
                              sx={styles.btn_appbar_search_icon}
                            >
                              <SearchIcon id="ico_search" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />

              <Button
                id="btn_add"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={addToView}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={Productdata.columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={Productdata.name}
            gridid="productcode"
            processRowUpdate={processRowUpdate}
          />
        </Box>
      </Box>
    </>
  );
}

export default ViewProduct;